import { useValue } from "@tldraw/state-react";
import { Box, Check, Download, Maximize2, MessageSquarePlus, Move3d, RotateCcw } from "lucide-react";
import type { Editor } from "./canvas";
import { readSceneSpec } from "../shared/scene-spec";
import { SCENE_VIEWS } from "../shared/schema";
import {
  downloadScene,
  liveScenes,
  requestScene,
  scenePick,
  type SceneShape,
} from "./SceneShape";
import { describeElement } from "./SceneViewer";
import type { SceneView } from "./scene-engine";

const VIEW_LABELS: Record<Exclude<(typeof SCENE_VIEWS)[number], "custom">, string> = {
  iso: "3/4",
  front: "Front",
  top: "Top",
  eye: "Eye level",
};

/** Floating controls for a selected 3D model: orbit, views, full screen and download. */
export function SceneToolbar({ editor, canEdit }: { editor: Editor; canEdit: boolean }) {
  const state = useValue(
    "scene toolbar",
    () => {
      const shape = editor.getOnlySelectedShape();
      if (!shape || shape.type !== "scene") return null;
      const bounds = editor.getShapePageBounds(shape);
      if (!bounds || !bounds.collides(editor.getViewportPageBounds())) return null;
      const viewport = editor.getViewportScreenBounds();
      const top = editor.pageToScreen({ x: bounds.center.x, y: bounds.minY });
      const bottom = editor.pageToScreen({ x: bounds.center.x, y: bounds.maxY });
      const editing = editor.getEditingShapeId() === shape.id;
      const height = editing ? 96 : 50;
      const above = top.y - viewport.y - height - 10;
      const below = bottom.y - viewport.y + 12;
      // Above the model, else below it, else pinned under the header: never on the tool dock.
      const y = above >= 86 ? above : below + height <= viewport.h - 96 ? below : 86;
      const x = Math.max(200, Math.min(viewport.w - 200, top.x - viewport.x));
      return { shape: shape as SceneShape, editing, x, y };
    },
    [editor],
  );
  const picked = useValue("scene pick", () => scenePick.get(), []);
  if (!state) return null;
  const { shape, editing } = state;
  const pick = picked?.shapeId === shape.id ? picked.pick : null;
  const spec = readSceneSpec(shape.props.spec);

  const setView = (view: SceneView) => {
    const live = liveScenes.get(shape.id);
    if (editing && live) live.setView(view);
    else if (canEdit)
      editor.updateShape({ id: shape.id, type: "scene", props: { view, camera: [] } });
  };
  const orbit = () => {
    editor.setCurrentTool("select");
    editor.setEditingShape(shape.id);
  };
  const done = () => {
    editor.setEditingShape(null);
    editor.focus();
  };

  return (
    <div
      className="pdf-object-toolbar floating-surface scene-toolbar"
      style={{ left: state.x, top: state.y }}
      onPointerDown={(e) => e.stopPropagation()}
      role="toolbar"
      aria-label={`${shape.props.name} 3D controls`}
    >
      <div className="pdf-control-row">
        {!editing && canEdit && (
          <button className="scene-toolbar-primary" onClick={orbit} title="Orbit in place (double-click)">
            <Move3d size={16} /> Orbit
          </button>
        )}
        {(editing || canEdit) && (
          <div className="scene-toolbar-views" role="group" aria-label="Views">
            {(Object.keys(VIEW_LABELS) as (keyof typeof VIEW_LABELS)[]).map((view) => (
              <button
                key={view}
                aria-pressed={!editing && shape.props.view === view}
                onClick={() => setView(view)}
              >
                {VIEW_LABELS[view]}
              </button>
            ))}
          </div>
        )}
        {editing ? (
          <>
            <button onClick={() => liveScenes.get(shape.id)?.replay()} title="Replay the build" aria-label="Replay the build">
              <RotateCcw size={15} />
            </button>
            <button className="scene-toolbar-primary" onClick={done}>
              <Check size={15} /> Done
            </button>
          </>
        ) : (
          <>
            <span className="pdf-control-divider" />
            <button onClick={() => requestScene({ action: "open", shapeId: shape.id })} title="Open full screen" aria-label="Open full screen">
              <Maximize2 size={15} />
            </button>
            {spec && (
              <button onClick={() => void downloadScene(spec)} title="Download as GLB" aria-label="Download as GLB">
                <Download size={15} />
              </button>
            )}
          </>
        )}
      </div>
      {editing && (
        <div className="scene-toolbar-pick" aria-live="polite">
          {pick ? (
            <>
              <span>
                <strong>{pick.element.name}</strong>
                {describeElement(pick.element)}
              </span>
              {spec?.plan && (
                <button onClick={() => requestScene({ action: "comment", shapeId: shape.id, pick })}>
                  <MessageSquarePlus size={14} /> Comment on plan
                </button>
              )}
            </>
          ) : (
            <span className="scene-toolbar-hint">
              <Box size={13} /> Click part of the model to see what it is
            </span>
          )}
        </div>
      )}
    </div>
  );
}
